import { useState, useEffect, memo, lazy, Suspense } from "react";
import { Hero } from "@/components/Hero";
import { About } from "@/components/About";
import { ThemeToggle } from "@/components/ThemeToggle";
import { SEO } from "@/components/SEO";
import { Analytics } from "@/components/Analytics";
import { HeroSkeleton, AboutSkeleton, PortfolioSkeleton, ContactSkeleton } from "@/components/LoadingSkeleton";
import { storage } from "@/lib/storage";
import { PortfolioItem, PersonalInfo } from "@/types/portfolio";
import { Button } from "@/components/ui/button";
import { ChevronUp, AlertCircle } from "lucide-react";
import { useThemeConfig } from "@/hooks/useThemeConfig";
import { supabase } from "@/integrations/supabase/client";
import { logError } from "@/lib/logger";
import { Link } from "react-router-dom";

// Lazy load below-the-fold components to reduce initial bundle size
const Portfolio = lazy(() => import("@/components/Portfolio").then(m => ({ default: m.Portfolio })));
const Testimonials = lazy(() => import("@/components/Testimonials").then(m => ({ default: m.Testimonials })));
const Contact = lazy(() => import("@/components/Contact").then(m => ({ default: m.Contact })));

// Memoize above-the-fold components for performance
const MemoizedHero = memo(Hero);
const MemoizedAbout = memo(About);
const MemoizedPortfolio = memo(Portfolio);
const MemoizedTestimonials = memo(Testimonials);
const MemoizedContact = memo(Contact);


const Index = () => {
  useThemeConfig();
  const [personalInfo, setPersonalInfo] = useState<PersonalInfo | null>(null);
  const [portfolioItems, setPortfolioItems] = useState<PortfolioItem[]>([]);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [seoConfig, setSeoConfig] = useState<any>(null);
  const [loadingTooLong, setLoadingTooLong] = useState(false);
  const [siteConfig, setSiteConfig] = useState<any>(null);

  useEffect(() => {
    const loadData = async () => {
      console.log('[Index] Starting data load...');
      setIsLoading(true);
      setError(null);
      setLoadingTooLong(false);
      
      const timeout = new Promise((_, reject) =>
        setTimeout(() => {
          console.error('[Index] TIMEOUT after 30 seconds');
          reject(new Error('Data loading timeout'));
        }, 30000)
      );

      try {
        console.log('[Index] Fetching personal info, portfolio, SEO, site config...');
        const dataPromise = Promise.all([
          storage.getPersonalInfo(),
          storage.getPortfolioItems(),
          supabase.from('seo_config').select('*').limit(1).maybeSingle(),
          storage.getSiteConfig()
        ]);

        const [info, items, seoData, config] = await Promise.race([dataPromise, timeout]) as any;
        
        console.log('[Index] Data loaded:', { info, items, seoData, config });
        
        setPersonalInfo(info);
        setPortfolioItems(items);
        setSeoConfig(seoData?.data || null);
        setSiteConfig(config);
      } catch (err) {
        console.error('[Index] Error loading data:', err);
        logError(err, 'Index.loadData');
        setError('Er ging iets mis bij het laden van de pagina. Open de browser console (F12) voor meer details.');
      } finally {
        console.log('[Index] Loading complete, isLoading set to false');
        setIsLoading(false);
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    if (!isLoading) return;
    const timer = setTimeout(() => setLoadingTooLong(true), 5000);
    return () => clearTimeout(timer);
  }, [isLoading]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen">
        <div className="fixed top-6 right-6 z-50">
          <ThemeToggle />
        </div>
        <HeroSkeleton />
        <AboutSkeleton />
        <PortfolioSkeleton />
        <ContactSkeleton />
        {loadingTooLong && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 text-center text-sm text-muted-foreground animate-fade-in bg-background/95 backdrop-blur px-4 py-2 rounded-lg border">
            Dit duurt langer dan verwacht...
          </div>
        )}
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center space-y-4 max-w-md">
          <AlertCircle className="w-12 h-12 text-destructive mx-auto" />
          <h2 className="text-2xl font-bold">Kan pagina niet laden</h2>
          <p className="text-muted-foreground">{error}</p>
          <div className="text-xs text-muted-foreground mt-2 p-3 bg-muted/50 rounded-lg">
            Open de browser console (F12) voor meer details
          </div>
          <Button onClick={() => window.location.reload()}>
            Probeer opnieuw
          </Button>
        </div>
      </div>
    );
  }

  if (!personalInfo) {
    return (
      <div className="min-h-screen">
        <SEO title="Portfolio" description="Bekijk mijn portfolio en projecten" />
        <div className="fixed top-6 right-6 z-50">
          <ThemeToggle />
        </div>
        <main className="min-h-[70vh] flex items-center justify-center px-4">
          <div className="max-w-md w-full text-center space-y-4">
            <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto" />
            <h1 className="text-2xl font-bold">Nog geen profiel gevonden</h1>
            <p className="text-muted-foreground">
              Er is nog geen openbare persoonlijke informatie geconfigureerd. Ga naar de admin om je gegevens in te vullen.
            </p>
            <Button asChild>
              <Link to="/admin">Naar Admin</Link>
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <SEO
        title={`${personalInfo.first_name} ${personalInfo.last_name} - Portfolio`}
        description={seoConfig?.meta_description || personalInfo.oneliner || personalInfo.bio}
        image={seoConfig?.og_image || personalInfo.avatar}
      />
      <Analytics />

      <div className="fixed top-6 right-6 z-50">
        <ThemeToggle />
      </div>

      <main>
        <MemoizedHero info={personalInfo} siteConfig={siteConfig} />
        
        {siteConfig?.show_about !== false && (
          <MemoizedAbout info={personalInfo} />
        )}
        
        {siteConfig?.show_portfolio !== false && (
          <Suspense fallback={<PortfolioSkeleton />}>
            <MemoizedPortfolio items={portfolioItems} />
          </Suspense>
        )}
        
        {siteConfig?.show_testimonials !== false && (
          <Suspense fallback={<div className="py-20 bg-muted/30"><div className="container mx-auto px-4 text-center text-muted-foreground">Laden...</div></div>}>
            <MemoizedTestimonials />
          </Suspense>
        )}
        
        {siteConfig?.show_contact !== false && (
          <Suspense fallback={<ContactSkeleton />}>
            <MemoizedContact info={personalInfo} />
          </Suspense>
        )}
      </main>

      {/* Scroll to top button */}
      {showScrollTop && siteConfig?.show_scroll_to_top !== false && (
        <Button
          size="icon"
          className="fixed bottom-6 right-6 rounded-full shadow-lg animate-fade-in hover-lift"
          onClick={scrollToTop}
          aria-label="Scroll naar boven"
        >
          <ChevronUp className="h-5 w-5" />
        </Button>
      )}

      {siteConfig?.show_admin_link !== false && (
        <Link
          to="/admin"
          className="fixed bottom-6 left-6 w-2 h-2 rounded-full bg-muted-foreground/20 hover:bg-muted-foreground/40 transition-colors"
          aria-label="Admin toegang"
          tabIndex={-1}
        />
      )}
    </div>
  );
};

export default Index;
